/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react';
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TextField,
  Typography,
  FormControl,
  MenuItem,
  DialogContentText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { OrderItemDetails, OrderItemUpdate } from '../../../../app/services/api/orderManagementClient';
import { applyRoundAndFormat } from '../../../../utils';

interface SubmitModalProps {
  openModal: boolean;
  onConfirm: (item: OrderItemUpdate) => void;
  selectedItem: OrderItemDetails | undefined;
  onCancel: () => void;
  currencyCode: string | undefined;
}

const ItemPriceOverrideModal: React.FC<SubmitModalProps> = ({
  openModal,
  onCancel,
  selectedItem,
  onConfirm,
  currencyCode,
}) => {
  const { t } = useTranslation('orderDetails');
  const textFieldMargin = { mb: 3 };
  const [editedItem, setEditedItem] = React.useState<OrderItemUpdate | undefined>(undefined);
  const [showConfirmModal, setShowConfirmModal] = React.useState(false);

  React.useEffect(() => {
    if (selectedItem) {
      setEditedItem({ id: selectedItem.itemId, quantity: selectedItem.quantity } as OrderItemUpdate);
    }
  }, [selectedItem]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    fieldName: string,
  ) => {
    setEditedItem({ ...editedItem, [fieldName]: e.target.value } as OrderItemUpdate);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
    onCancel();
  };

  const onSave = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmSave = () => {
    setShowConfirmModal(false);
    onConfirm(editedItem as OrderItemUpdate);
  };

  const handleCancelConfirm = () => {
    setShowConfirmModal(false);
  };

  return (
    <>
      <Dialog open={openModal}
        onClose={handleCancel}
        maxWidth="sm"
        fullWidth>
        <DialogTitle sx={{ m: 4, p: 4 }}>
          {t('itemPriceOverrideModal.overrideCasePrice')}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleCancel}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.custom.gray[800],
          }}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent>
          <Grid container
            spacing={2}
            sx={{ pt: '10px' }}>
            <Grid item
              xs={6}>
              <Typography sx={{ fontWeight: 'bolder' }}>{t('itemPriceOverrideModal.productId')}</Typography>
            </Grid>
            <Grid item
              xs={6}>
              <Typography>{selectedItem?.sku}</Typography>
            </Grid>

            <Grid item
              xs={6}>
              <Typography sx={{ fontWeight: 'bolder' }}>{t('itemPriceOverrideModal.description')}</Typography>
            </Grid>
            <Grid item
              xs={6}>
              <Typography>{selectedItem?.name}</Typography>
            </Grid>

            <Grid item
              xs={6}>
              <Typography sx={{ fontWeight: 'bolder' }}>{t('itemPriceOverrideModal.originalCasePrice')}</Typography>
            </Grid>
            <Grid item
              xs={6}>
              <Typography>{applyRoundAndFormat(selectedItem?.priceOverrideOriginalPrice ?? selectedItem?.price ?? 0, currencyCode)}</Typography>
            </Grid>

            <Grid item
              xs={12}
              sx={{ mt: '20px' }}>
              <FormControl fullWidth
                size="small">
                <TextField
                  InputLabelProps={{ shrink: true }}
                  required
                  type="number"
                  label={t('itemPriceOverrideModal.overridePrice')}
                  value={editedItem?.overridePrice ?? ''}
                  onChange={(e) => handleChange(e, 'overridePrice')}
                  sx={textFieldMargin}
                />
              </FormControl>
            </Grid>

            <Grid item
              xs={12}
              sx={{ mt: '10px' }}>
              <FormControl fullWidth
                size="small">
                <TextField
                  InputLabelProps={{ shrink: true }}
                  select
                  label={t('itemPriceOverrideModal.overrideReason')}
                  value={editedItem?.overrideReason ?? ''}
                  onChange={(e) => handleChange(e, 'overrideReason')}
                  sx={textFieldMargin}
                >
                  <MenuItem value="1">Promo Offer</MenuItem>
                  <MenuItem value="2">Account Manager Decision</MenuItem>
                </TextField>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button variant="secondary"
            onClick={handleCancel}
            autoFocus>
            {t('cancel')}
          </Button>
          <Button variant="primary"
            onClick={onSave}
            disabled={
              !editedItem?.overridePrice || // disables if price is empty or zero
              !editedItem?.overrideReason || // disables if reason is not selected
              editedItem?.overridePrice < 0 ||
              editedItem?.overridePrice == selectedItem?.price
            } 
            autoFocus>
            {t('save')}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Confirmation Modal */}
      <Dialog
        open={showConfirmModal}
        onClose={handleCancelConfirm}
        maxWidth="xs"
      >
        <DialogTitle>
          {t('itemPriceOverrideModal.confirmOverrideTitle', 'Save Price Override')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('itemPriceOverrideModal.confirmOverrideText', 'Are you sure you want to override the case price?')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirm}
            variant="secondary">
            {t('cancel')}
          </Button>
          <Button onClick={handleConfirmSave} 
            variant="primary"
            autoFocus>
            {t('confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ItemPriceOverrideModal;
